import { FC, useCallback, useEffect, useState } from 'react';

import styled from 'styled-components';

import { useBreakpoint } from '@contexts/breakpoints';
import { useCalendarHoliday } from '@components/DatePicker/DateRangePicker/useCalendarHoliday';
import SearchBarDateRangeInput from '@components/ui/search/SearchBarInput/SearchBarDateInput/SearchBarRangeDatePickerInput/SearchBarDateRangePickerInput';
import { getDateRangePickerPlaceholder } from '@components/views/flight/listing/SearchBar/DateSelect/utils';
import {
  DateType,
  getCurrentDate,
  isInclusivelyAfterDay,
} from '@utils/dateUtil';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import { getViewportConstrainedPopoverLocation } from '@components/ui/search/SearchBarInput/getViewportConstrainedPopoverLocation';
import type { DateRangeApplyResolver } from '@components/DatePicker/DateRangePicker/DateRangePicker';

import ResponsiveDateRangeSelect from './responsiveSearchDrawerItem/ResponsiveDateRangeSelect';

const BookerSearchBarDateRangeInputWrapper = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
`;

export const HOTEL_DESKTOP_HORIZONTAL_MONTH_PADDING = 20;

interface IHotelBookerDatePicker {
  setLocalStartDate: React.Dispatch<React.SetStateAction<DateType | null>>;
  setLocalEndDate: React.Dispatch<React.SetStateAction<DateType | null>>;
  localStartDate: DateType | null;
  localEndDate: DateType | null;
  dateChange: (dates?: {
    startDate?: DateType | null;
    endDate?: DateType | null;
  }) => void;
  isSubmit?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  isErrorDateSelection?: boolean;
  datepickerVisible?: boolean;
  onDatepickerClose?(): void;
  showLabel?: boolean;
  isTravelPlanning?: boolean;
  resolveRangeOnApply?: DateRangeApplyResolver;
}

const HotelBookerDatePicker: FC<IHotelBookerDatePicker> = ({
  dateChange,
  setLocalEndDate,
  setLocalStartDate,
  localStartDate,
  localEndDate,
  isSubmit,
  onFocus,
  onBlur,
  isErrorDateSelection = false,
  datepickerVisible = false,
  onDatepickerClose,
  showLabel = false,
  isTravelPlanning,
  resolveRangeOnApply,
}) => {
  const [showDatepicker, setShowDatepicker] = useState(false);
  const [isCalendarReady, setIsCalendarReady] = useState(false);
  const isHolidayEnabled = !isTravelPlanning;
  const setDates = ({
    startDate,
    endDate,
  }: {
    startDate: DateType | null;
    endDate: DateType | null;
  }) => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
    if (startDate && endDate) dateChange({ startDate, endDate });
  };
  const { xl } = useBreakpoint();
  // MAP-takvim-guncelleme: holiday markers are opt-in for the HOTEL search bar
  // only. React Query dedupes the request across every hotel date component via
  // the shared ['calendarHolidays'] key, so calling it per-component is safe.
  const { data: holidayData } = useCalendarHoliday();

  const handleCalendarReady = useCallback(() => {
    setIsCalendarReady(true);
  }, []);

  const handleDesktopPopoverVisibleChange = useCallback((visible: boolean) => {
    setIsCalendarReady(false);
    setShowDatepicker(visible);
  }, []);

  useEffect(() => {
    if (datepickerVisible) {
      setIsCalendarReady(false);
      setShowDatepicker(true);
      onDatepickerClose?.();
    }
  }, [datepickerVisible]);

  return (
    <BookerSearchBarDateRangeInputWrapper>
      {xl || xl === undefined ? (
        <SearchBarDateRangeInput
          label='Ne Kadar Kalacaksınız?'
          dateProps={{
            startDate: localStartDate,
            endDate: localEndDate,
            onDatesChange: setDates,
            isOutsideRange: (day) =>
              !isInclusivelyAfterDay(day, getCurrentDate()),
            initialData: isHolidayEnabled ? holidayData : undefined,
            horizontalMonthPadding: HOTEL_DESKTOP_HORIZONTAL_MONTH_PADDING,
            // Travel Planning Hotel modal is out of MAP holiday scope. Keep
            // shared desktop footer/viewport behavior, but do not render the
            // holiday tabs or markers in this caller.
            isHoliday: isHolidayEnabled,
            onReady: handleCalendarReady,
            resolveRangeOnApply,
          }}
          inputComponent='shopAndFlyHomePageHotel'
          isSubmit={isSubmit}
          onFocus={onFocus}
          onBlur={onBlur}
          error={isErrorDateSelection}
          popoverContentReady={isCalendarReady}
          popoverProps={{
            isOpen: showDatepicker,
            onVisibleChange: handleDesktopPopoverVisibleChange,
            contentLocation: isTravelPlanning
              ? getViewportConstrainedPopoverLocation
              : undefined,
          }}
          showLabel={showLabel}
        />
      ) : (
        <LocationAnimationErrorBox hasError={isErrorDateSelection}>
          <ResponsiveDateRangeSelect
            startDate={localStartDate}
            endDate={localEndDate}
            onDatesChange={setDates}
            isTravelPlanning={isTravelPlanning}
            title='Gidiş-Dönüş Tarihiniz?'
            subTitle={getDateRangePickerPlaceholder(
              localStartDate,
              localEndDate
            )}
            placeholder='Gidiş - Dönüş Tarihleri'
            drawerTitle='Tarih Arama'
            arrowIcon={true}
            inputComponent='shopAndFlyHomePageHotel'
            isSubmit={isSubmit}
            popoverProps={{
              isOpen: showDatepicker,
              onVisibleChange: setShowDatepicker,
            }}
            showLabel={showLabel}
          />
        </LocationAnimationErrorBox>
      )}
    </BookerSearchBarDateRangeInputWrapper>
  );
};
export default HotelBookerDatePicker;
